import { useState, useEffect } from 'react'

import type { ReactNode } from 'react'

const DEFAULT_FADE_TIMEOUT = 450

interface FadeTransitionProps {
  show: boolean
  children: ReactNode
  className?: string
  openDelay?: number
  closeDelay?: number
}

function FadeTransition({ show, children, className, openDelay = DEFAULT_FADE_TIMEOUT, closeDelay = DEFAULT_FADE_TIMEOUT }: FadeTransitionProps) {
  const [isMounted, setIsMounted] = useState(false)
  const [isFadedIn, setIsFadedIn] = useState(false)

  // Handle fade animations
  useEffect(() => {
    let fadeTimer: ReturnType<typeof setTimeout> | undefined
    let raf1: number | undefined
    let raf2: number | undefined
    let raf3: number | undefined

    if (show) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsMounted(true)

      raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => {
          setIsFadedIn(true)
        })
      })
    } else {
      // Wrap in RAF to avoid ESLint warning
      raf3 = requestAnimationFrame(() => {
        setIsFadedIn(false)
      })

      fadeTimer = setTimeout(() => setIsMounted(false), closeDelay)
    }

    return () => {
      if (fadeTimer) clearTimeout(fadeTimer)
      if (raf1) cancelAnimationFrame(raf1)
      if (raf2) cancelAnimationFrame(raf2)
      if (raf3) cancelAnimationFrame(raf3)
    }
  }, [show, closeDelay])

  if (!isMounted) return null

  return (
    <div
      className={className}
      style={{
        transition: `opacity ${isFadedIn ? openDelay : closeDelay}ms ease-in-out`,
        opacity: isFadedIn ? 1 : 0,
      }}
    >
      {children}
    </div>
  )
}

export default FadeTransition